import type { SalesFlowEdge, SalesFlowNode, SalesFlowSettingsData, SalesFlowTrigger } from '@/lib/api/sales/flows-api'

export interface FlowTemplate {
    id: string
    name: string
    description: string
    settings: SalesFlowSettingsData
    triggers: SalesFlowTrigger[]
    nodes: SalesFlowNode[]
    edges: SalesFlowEdge[]
}

const node = (key: string, type: SalesFlowNode['type'], config: Record<string, any>, x: number, y: number): SalesFlowNode => ({
    key,
    type,
    config,
    positionX: x,
    positionY: y,
})

const COL = 400
const HOUR = 60

/**
 * Modelos prontos. Etapas e setores ficam em branco de propósito: variam por
 * organização, e o validador aponta o que falta escolher antes de ativar.
 */
export const FLOW_TEMPLATES: FlowTemplate[] = [
    {
        id: 'blank',
        name: 'Em branco',
        description: 'Só o início. Monte o fluxo do zero.',
        settings: {},
        triggers: [{ type: 'MANUAL' }],
        nodes: [node('start', 'START', {}, 0, 0)],
        edges: [],
    },
    {
        id: 'stage-follow-up',
        name: 'Follow-up por etapa',
        description:
            'A oportunidade entra na etapa de follow-up e recebe até três mensagens, com esperas entre elas. Se o contato responder, volta para o atendimento; se não responder, vai para perdido.',
        settings: { exitRules: { onDealClosed: true, onAppointmentCreated: true }, launchable: true },
        triggers: [{ type: 'STAGE_ENTERED', pipelineId: null, stageId: null, delayMinutes: 2 }],
        nodes: [
            node('start', 'START', {}, 0, 120),
            node('msg1', 'SEND_WHATSAPP', { content: 'Oi, {{lead.name}}! Percebi que nossa conversa ficou parada. Se ficou com alguma dúvida ou quiser retomar o atendimento, estou aqui para ajudar.' }, COL, 120),
            node('wait1', 'PAUSE', { timeoutMinutes: 24 * HOUR, waitForReply: true }, COL * 2, 120),
            node('msg2', 'SEND_WHATSAPP', { content: 'Oi! Ainda estamos te aguardando para dar sequência no seu atendimento. Algumas agendas ficam cheias rápido: se quiser garantir seu horário ou tirar uma dúvida, é só responder.' }, COL * 3, 260),
            node('wait2', 'PAUSE', { timeoutMinutes: 72 * HOUR, waitForReply: true }, COL * 4, 260),
            node('msg3', 'SEND_WHATSAPP', { content: 'Oi, {{lead.name}}. Como nossa conversa ficou parada, vamos encerrar este atendimento por enquanto. Se quiser retomar ou tiver qualquer dúvida no futuro, será um prazer ajudar.' }, COL * 5, 400),
            node('wait3', 'PAUSE', { timeoutMinutes: 24 * HOUR, waitForReply: true }, COL * 6, 400),
            node('lost', 'MOVE_STAGE', { pipelineId: null, stageId: null }, COL * 7, 400),
            node('back', 'MOVE_STAGE', { pipelineId: null, stageId: null }, COL * 5, -60),
            node('notify', 'NOTIFY_USER', { title: 'Cliente respondeu o follow-up', message: '{{lead.name}} respondeu o follow-up.' }, COL * 6, -60),
        ],
        edges: [
            { fromKey: 'start', handle: 'next', toKey: 'msg1' },
            { fromKey: 'msg1', handle: 'next', toKey: 'wait1' },
            { fromKey: 'wait1', handle: 'timeout', toKey: 'msg2' },
            { fromKey: 'wait1', handle: 'replied', toKey: 'back' },
            { fromKey: 'msg2', handle: 'next', toKey: 'wait2' },
            { fromKey: 'wait2', handle: 'timeout', toKey: 'msg3' },
            { fromKey: 'wait2', handle: 'replied', toKey: 'back' },
            { fromKey: 'msg3', handle: 'next', toKey: 'wait3' },
            { fromKey: 'wait3', handle: 'timeout', toKey: 'lost' },
            { fromKey: 'wait3', handle: 'replied', toKey: 'back' },
            { fromKey: 'back', handle: 'next', toKey: 'notify' },
        ],
    },
    {
        id: 'new-lead-welcome',
        name: 'Boas-vindas ao lead novo',
        description: 'Lead criado: mensagem de boas-vindas no horário comercial e aviso para o responsável.',
        settings: { activeWindow: { timezone: 'America/Sao_Paulo', days: [1, 2, 3, 4, 5, 6], from: '08:00', to: '19:00' } },
        triggers: [{ type: 'EVENT', event: 'LEAD_CREATED' }],
        nodes: [
            node('start', 'START', {}, 0, 0),
            node('welcome', 'SEND_WHATSAPP', { content: 'Olá, {{lead.name}}! Recebemos seu contato e já vamos te atender.' }, COL, 0),
            node('notify', 'NOTIFY_USER', { title: 'Lead novo', message: '{{lead.name}} acabou de chegar.' }, COL * 2, 0),
        ],
        edges: [
            { fromKey: 'start', handle: 'next', toKey: 'welcome' },
            { fromKey: 'welcome', handle: 'next', toKey: 'notify' },
            { fromKey: 'welcome', handle: 'failed', toKey: 'notify' },
        ],
    },
    {
        id: 'billing-paid-move',
        name: 'Mover ao pagar',
        description: 'Cobrança paga: leva a oportunidade para a etapa escolhida. Assume a movimentação do funil na cobrança paga.',
        settings: {},
        triggers: [{ type: 'EVENT', event: 'BILLING_PAID' }],
        nodes: [node('start', 'START', {}, 0, 0), node('move', 'MOVE_STAGE', { pipelineId: null, stageId: null }, COL, 0)],
        edges: [{ fromKey: 'start', handle: 'next', toKey: 'move' }],
    },
]
